import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface Newsletter {
  id: string
  title: string
  subject?: string
  status: "draft" | "pending_approval" | "approved" | "sent" | "failed"
  previewUrl?: string
  approvedBy?: string
  approvedAt?: string
  sentAt?: string
}

interface ApprovalWorkflowProps {
  newsletter: Newsletter
  onStatusChange: () => void
}

export default function ApprovalWorkflow({ newsletter, onStatusChange }: ApprovalWorkflowProps) {
  const [sendImmediately, setSendImmediately] = useState(false)
  const [recipients, setRecipients] = useState(["team@company.com"])
  const [newRecipient, setNewRecipient] = useState("")

  const requestApproval = trpc.newsletter.requestApproval.useMutation({
    onSuccess: () => {
      onStatusChange()
      alert("Newsletter submitted for approval successfully!")
    },
    onError: (error) => {
      alert(`Failed to request approval: ${error.message}`)
    },
  })

  const approveNewsletter = trpc.newsletter.approve.useMutation({
    onSuccess: (result) => {
      onStatusChange()
      if (result.sentImmediately) {
        alert("Newsletter approved and sent successfully!")
      } else {
        alert("Newsletter approved successfully!")
      }
    },
    onError: (error) => {
      alert(`Failed to approve newsletter: ${error.message}`)
    },
  })

  const sendNewsletter = trpc.newsletter.send.useMutation({
    onSuccess: (result) => {
      onStatusChange()
      alert(`Newsletter sent successfully to ${result.sentTo.length} recipients!`)
    },
    onError: (error) => {
      alert(`Failed to send newsletter: ${error.message}`)
    },
  })

  const handleRequestApproval = () => {
    if (confirm("Submit this newsletter for approval? You won't be able to edit it after submission.")) {
      requestApproval.mutate(newsletter.id)
    }
  }

  const handleApprove = () => {
    if (confirm(`Approve this newsletter${sendImmediately ? " and send it immediately" : ""}?`)) {
      approveNewsletter.mutate({
        newsletterId: newsletter.id,
        sendImmediately,
      })
    }
  }

  const handleSend = () => {
    if (recipients.length === 0) {
      alert("Please add at least one recipient.")
      return
    }

    if (confirm(`Send this newsletter to ${recipients.length} recipient(s)?`)) {
      sendNewsletter.mutate({
        newsletterId: newsletter.id,
        recipients,
      })
    }
  }

  const addRecipient = () => {
    if (newRecipient && newRecipient.includes("@") && !recipients.includes(newRecipient)) {
      setRecipients([...recipients, newRecipient])
      setNewRecipient("")
    }
  }

  const removeRecipient = (email: string) => {
    setRecipients(recipients.filter((r) => r !== email))
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "bg-foregroundx text-gray-800"
      case "pending_approval":
        return "bg-yellow-100 text-yellow-800"
      case "approved":
        return "bg-green-100 text-green-800"
      case "sent":
        return "bg-blue-100 text-blue-800"
      case "failed":
        return "bg-red-100 text-destructive"
      default:
        return "bg-foregroundx text-gray-800"
    }
  }

  const getStatusMessage = () => {
    switch (newsletter.status) {
      case "draft":
        return {
          title: "Draft Status",
          message: "This newsletter is still in draft mode. Complete the content and submit for approval.",
          action: "Submit for Approval",
        }
      case "pending_approval":
        return {
          title: "Pending Approval",
          message: "This newsletter is waiting for approval. Review the content and approve to proceed.",
          action: "Approve Newsletter",
        }
      case "approved":
        return {
          title: "Approved",
          message: "This newsletter has been approved and is ready to send.",
          action: "Send Newsletter",
        }
      case "sent":
        return {
          title: "Sent",
          message: `This newsletter was sent on ${
            newsletter.sentAt ? new Date(newsletter.sentAt).toLocaleString() : "Unknown date"
          }.`,
          action: null,
        }
      case "failed":
        return {
          title: "Failed",
          message: "There was an error sending this newsletter. Please try again.",
          action: "Retry Sending",
        }
      default:
        return {
          title: "Unknown Status",
          message: "Unknown newsletter status.",
          action: null,
        }
    }
  }

  const statusInfo = getStatusMessage()

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <div className="bg-background rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold ">Newsletter Status</h2>
          <span
            className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(newsletter.status)}`}
          >
            {newsletter.status.replace("_", " ").toUpperCase()}
          </span>
        </div>

        <div className="bg-foregroundx rounded-lg p-4">
          <h3 className="text-lg font-medium  mb-2">{statusInfo.title}</h3>
          <p className=" mb-4">{statusInfo.message}</p>

          {newsletter.approvedBy && newsletter.approvedAt && (
            <div className="text-sm text-gray-500 mb-4">
              Approved on {new Date(newsletter.approvedAt).toLocaleString()}
            </div>
          )}

          {newsletter.previewUrl && (
            <div className="mb-4">
              <a
                href={newsletter.previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center  hover:text-blue-800"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                View Preview
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Workflow Actions */}
      {newsletter.status === "draft" && (
        <div className="bg-background rounded-lg shadow p-6">
          <h3 className="text-lg font-medium  mb-4">Submit for Approval</h3>
          <p className=" mb-4">
            Once you submit this newsletter for approval, you won&apos;t be able to edit the content. Make sure
            everything is ready before proceeding.
          </p>

          <button
            onClick={handleRequestApproval}
            disabled={requestApproval.isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {requestApproval.isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Submit for Approval
              </>
            )}
          </button>
        </div>
      )}

      {newsletter.status === "pending_approval" && (
        <div className="bg-background rounded-lg shadow p-6">
          <h3 className="text-lg font-medium  mb-4">Approve Newsletter</h3>
          <p className=" mb-4">
            Review the newsletter content and approve it for sending. You can choose to send it immediately or approve
            it for later sending.
          </p>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={sendImmediately}
                onChange={(e) => setSendImmediately(e.target.checked)}
                className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-primary">Send immediately after approval</span>
            </label>
          </div>

          <button
            onClick={handleApprove}
            disabled={approveNewsletter.isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {approveNewsletter.isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Approving...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {sendImmediately ? "Approve & Send" : "Approve Newsletter"}
              </>
            )}
          </button>
        </div>
      )}

      {newsletter.status === "approved" && (
        <div className="bg-background rounded-lg shadow p-6">
          <h3 className="text-lg font-medium  mb-4">Send Newsletter</h3>
          <p className=" mb-4">
            This newsletter has been approved and is ready to send. Configure the recipients and send when ready.
          </p>

          {/* Recipients Management */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-primary mb-2">Recipients</label>

            <div className="flex gap-2 mb-3">
              <input
                type="email"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                placeholder="Enter email address..."
                className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    addRecipient()
                  }
                }}
              />
              <button
                onClick={addRecipient}
                className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-primary bg-background "
              >
                Add
              </button>
            </div>

            <div className="space-y-1">
              {recipients.map((email, index) => (
                <div key={index} className="flex items-center justify-between bg-foregroundx px-3 py-2 rounded-md">
                  <span className="text-sm text-primary">{email}</span>
                  <button
                    onClick={() => removeRecipient(email)}
                    className="text-destructive hover:text-destructive text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleSend}
            disabled={sendNewsletter.isLoading || recipients.length === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {sendNewsletter.isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
                Send to {recipients.length} recipient{recipients.length !== 1 ? "s" : ""}
              </>
            )}
          </button>
        </div>
      )}

      {newsletter.status === "sent" && (
        <div className="bg-background rounded-lg shadow p-6">
          <h3 className="text-lg font-medium  mb-4">Newsletter Sent</h3>
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Successfully sent!</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Your newsletter has been delivered successfully.</p>
                  {newsletter.sentAt && <p className="mt-1">Sent on: {new Date(newsletter.sentAt).toLocaleString()}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {newsletter.status === "failed" && (
        <div className="bg-background rounded-lg shadow p-6">
          <h3 className="text-lg font-medium  mb-4">Sending Failed</h3>
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-destructive">Sending failed</h3>
                <div className="mt-2 text-sm text-destructive">
                  <p>There was an error sending your newsletter. Please try again.</p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleSend}
            disabled={sendNewsletter.isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
          >
            {sendNewsletter.isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Retrying...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Retry Sending
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}
